/**
 * Watching a project's changes, as decisions — UX-05.
 *
 * WHY THIS PAGE IS WORTH HAVING
 *
 * "Is realtime working?" has had no answer anywhere. The SDK can subscribe
 * and an app can be written to prove it, but somebody who wants to KNOW -
 * before building on it, or while wondering why their app is quiet - had
 * to write the app first.
 *
 * WHAT A TICKET COSTS
 *
 * Five minutes. A ticket is signed for one channel and expires, which is
 * the right trade for something handed to a browser and the wrong thing to
 * discover by watching a page go silent. So the page says when its
 * subscription will end, and ending is a normal outcome rather than an
 * error.
 *
 * WHAT SILENCE MEANS
 *
 * Nothing, and that is the hard part. A channel with no events looks
 * exactly like a broken subscription. The page separates "connected" from
 * "received something", because those are different facts and only the
 * first one is evidence that anything works.
 */

export type LiveEvent = {
  type: 'change' | 'message'
  /** For a change: the table. For a message: the channel. */
  subject: string
  detail: string
  at: string
}

export type Connection =
  | { kind: 'idle' }
  | { kind: 'connecting' }
  | { kind: 'open'; expiresAt: string }
  | { kind: 'closed'; reason: string }

/** The channel a table's changes are broadcast on. Matches the data plane. */
export const tableChannel = (table: string): string => `table:${table}`

/**
 * A bare name matching a real table means that table's row-change stream.
 * Custom channels remain available; this only removes the trap where typing
 * `notes` looked like watching the notes table but actually watched an
 * unrelated custom channel.
 */
export function resolveChannel(input: string, tables: readonly string[]): string {
  const value = input.trim()
  return tables.includes(value) ? tableChannel(value) : value
}

/**
 * What to say about a connection, given that silence proves nothing.
 *
 * "Connected" and "seen something" are deliberately separate sentences.
 * Merging them would let an empty channel read as a broken one, which is
 * the question this page exists to answer.
 */
export function connectionSummary(connection: Connection, events: number): string {
  switch (connection.kind) {
    case 'idle':
      return 'Not watching anything yet.'
    case 'connecting':
      return 'Opening the connection…'
    case 'open':
      return events === 0
        ? 'Connected and waiting. Nothing has happened on this channel yet — that is not a '
          + 'fault, it is a quiet table.'
        : `Connected. ${events} event${events === 1 ? '' : 's'} so far.`
    case 'closed':
      return connection.reason
  }
}

/**
 * Why a subscription ended, in words rather than a close code.
 *
 * Expiry is the common one and is not a failure: a ticket lasts five
 * minutes by design. Saying "error" about the expected outcome teaches
 * people to ignore the word.
 */
export function closeReason(code: number, sawEvents: boolean): string {
  if (code === 1000) return 'Stopped watching.'
  if (code === 1008) {
    return 'The connection was refused. The ticket was not valid for this channel, or the '
      + 'project is paused.'
  }
  return sawEvents
    ? 'The connection ended — a ticket lasts five minutes. Watch again for a fresh one.'
    : 'The connection ended before anything arrived. A ticket lasts five minutes; watch '
      + 'again if you were expecting something.'
}

/** One event, flattened for a table row. */
export function describeEvent(raw: unknown, at: string): LiveEvent | null {
  if (!raw || typeof raw !== 'object') return null
  const event = raw as Record<string, unknown>

  if (event['type'] === 'change') {
    const rows = Number(event['rows'] ?? 0)
    return {
      type: 'change',
      subject: String(event['table'] ?? 'unknown'),
      detail: `${String(event['operation'] ?? 'changed')} · ${rows} row${rows === 1 ? '' : 's'}`,
      at,
    }
  }
  if (event['type'] === 'message') {
    return {
      type: 'message',
      subject: String(event['channel'] ?? 'unknown'),
      // Whatever a customer published. Compact, because a page of JSON
      // pushes every other event off the screen.
      detail: compact(event['data']),
      at,
    }
  }
  // An event shape this page does not know. Dropping it silently would
  // make a working subscription look dead.
  return { type: 'message', subject: 'unknown', detail: compact(event), at }
}

function compact(value: unknown): string {
  const text = typeof value === 'string' ? value : JSON.stringify(value) ?? String(value)
  return text.length > 80 ? `${text.slice(0, 79)}…` : text
}

/** Newest first, and bounded: a busy table would otherwise fill the tab. */
export const MAX_EVENTS = 200
export const addEvent = (events: LiveEvent[], event: LiveEvent): LiveEvent[] =>
  [event, ...events].slice(0, MAX_EVENTS)
